import { PrePurchaseQuizPersonalData } from 'components/PageSections/AsyncProgramPPQ/FormFields/PersonalDataForm';
import { PrePurchaseQuizEmailData } from 'components/PageSections/PrePurchaseQuizModal/EligibilityScreens/YouAreEligible';
import { UserSelectionProps } from 'components/PageSections/PrePurchaseQuizModal/FormFields/OptionSelectForm';
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

const tracking = {
  startQuiz: () =>
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.START_QUIZ,
      },
    }),
  endQuiz: () =>
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.END_QUIZ,
      },
    }),
  nextStep: (step: number) =>
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: `${ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.NEXT_STEP} - step number ${
          step + 1
        }`,
      },
    }),
  prevStep: (step: number) =>
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: `${ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.PREV_STEP} - step number ${
          step + 1
        }`,
      },
    }),
  eligibilityEvent: (isEligible: boolean) =>
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: isEligible
          ? ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.YOU_ARE_ELIGIBLE
          : ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.YOU_ARE_NOT_ELIGIBLE,
      },
    }),
};

type AsyncProgramPPQStateType = {
  isModalOpen: boolean;
  isPrePurchaseQuizAvailable: boolean;
  currentStep: number;
  numberOfSteps: number;
  isQuizStarted: boolean;
  isQuizEnded: boolean;
  areControlsVisible: boolean;
  personalData: PrePurchaseQuizPersonalData | undefined;
  emailData: PrePurchaseQuizEmailData | undefined;
  isAnswerIneligible: boolean;
  questionsAndAnswers: UserSelectionProps[];
};

const DEFAULT_STATE: AsyncProgramPPQStateType = {
  isModalOpen: false,
  isPrePurchaseQuizAvailable: false,
  currentStep: 0,
  numberOfSteps: 0,
  isQuizStarted: false,
  isQuizEnded: false,
  personalData: undefined,
  emailData: undefined,
  areControlsVisible: true,
  isAnswerIneligible: false,
  questionsAndAnswers: [],
};

export type PrePurchaseQuizContextType = AsyncProgramPPQStateType & {
  toggleModal: (value: boolean) => void;
  // Use this to determine if the pre-purchase quiz is available in a page.
  setIsPrePurchaseQuizAvailable: (value: boolean) => void;
  nextStep: () => void;
  prevStep: () => void;
  setNumberOfSteps: (value: number) => void;
  startQuiz: () => void;
  endQuiz: () => void;
  toggleControlsVisibility: (value: boolean) => void;
  setPersonalData: (value: PrePurchaseQuizPersonalData) => void;
  setEmailData: (value: PrePurchaseQuizEmailData) => void;
  setIsAnswerIneligible: (value: boolean) => void;
  setQuestionsAndAnswers: (newSelection: UserSelectionProps) => void;
  resetState: () => void;
};

export const PRE_PURCHASE_DEFAULT_CONTEXT_VALUE: PrePurchaseQuizContextType = {
  // State values
  ...DEFAULT_STATE,

  // Functions
  toggleModal: () => {},
  setIsPrePurchaseQuizAvailable: () => {},
  nextStep: () => ({}),
  prevStep: () => ({}),
  setNumberOfSteps: () => ({}),
  startQuiz: () => ({}),
  endQuiz: () => ({}),
  toggleControlsVisibility: () => ({}),
  setPersonalData: () => ({}),
  setEmailData: () => ({}),
  resetState: () => ({}),
  setIsAnswerIneligible: () => ({}),
  setQuestionsAndAnswers: () => ({}),
};

const AsyncProgramPPQContext = createContext<PrePurchaseQuizContextType>(
  PRE_PURCHASE_DEFAULT_CONTEXT_VALUE,
);

export type AsyncProgramPPQContextProps = {
  children: ReactNode;
};

export const useAsyncProgramPPQContext = () =>
  useContext(AsyncProgramPPQContext);

export const AsyncProgramPPQContextProvider = ({
  children,
}: AsyncProgramPPQContextProps) => {
  // State values
  const [isModalOpen, setModalOpen] = useState(DEFAULT_STATE.isModalOpen);
  const [isPrePurchaseQuizAvailable, setPrePurchaseQuizAvailable] = useState(
    DEFAULT_STATE.isPrePurchaseQuizAvailable,
  );
  const [currentStep, setCurrentStep] = useState(DEFAULT_STATE.currentStep);
  const [numberOfSteps, setNumberOfSteps] = useState(
    DEFAULT_STATE.numberOfSteps,
  );
  const [isQuizStarted, setIsQuizStarted] = useState(
    DEFAULT_STATE.isQuizStarted,
  );
  const [isQuizEnded, setIsQuizEnded] = useState(DEFAULT_STATE.isQuizEnded);
  const [personalData, setPersonalData] = useState<
    PrePurchaseQuizPersonalData | undefined
  >(DEFAULT_STATE.personalData);
  const [emailData, setEmailData] = useState<
    PrePurchaseQuizEmailData | undefined
  >(DEFAULT_STATE.emailData);
  const [areControlsVisible, setAreControlsVisible] = useState(
    DEFAULT_STATE.areControlsVisible,
  );
  const [isAnswerIneligible, setIsAnswerIneligible] = useState(
    DEFAULT_STATE.isAnswerIneligible,
  );

  const [questionsAndAnswers, setQuestionsAndAnswersState] = useState<
    UserSelectionProps[]
  >(DEFAULT_STATE.questionsAndAnswers);

  const setQuestionsAndAnswers = (newSelection: UserSelectionProps) => {
    setQuestionsAndAnswersState((prevSelections) => {
      const existingQuestionIndex = prevSelections.findIndex(
        (selection) => selection.question === newSelection.question,
      );

      if (existingQuestionIndex !== -1) {
        const updatedSelections = [...prevSelections];
        updatedSelections[existingQuestionIndex] = newSelection;
        return updatedSelections;
      } else {
        return [...prevSelections, newSelection];
      }
    });
  };

  // Functions
  const nextStep = () => {
    const isLastStep = currentStep === numberOfSteps - 1;

    if (isLastStep) {
      endQuiz();
      return;
    }

    tracking.nextStep(currentStep);
    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    const isFirstStep = currentStep === 0;

    if (isFirstStep) {
      setIsQuizStarted(false);
      return;
    }

    if (isQuizEnded) {
      setIsQuizEnded(false);
    }

    tracking.prevStep(currentStep);
    setCurrentStep((prev) => prev - 1);
  };

  const toggleModal = (value: boolean) => setModalOpen(value);

  const setIsPrePurchaseQuizAvailable = (value: boolean) =>
    setPrePurchaseQuizAvailable(value);

  const setNumberOfStepsValue = (value: number) => setNumberOfSteps(value);

  const startQuiz = () => {
    setIsQuizStarted(true);
    tracking.startQuiz();
  };

  const endQuiz = () => {
    setIsQuizEnded(true);
    tracking.endQuiz();
  };

  const setPersonalDataValue = (value: PrePurchaseQuizPersonalData) =>
    setPersonalData(value);

  const setEmailDataValue = (value: PrePurchaseQuizEmailData) =>
    setEmailData(value);

  const toggleControlsVisibility = (value: boolean) =>
    setAreControlsVisible(value);

  const setIsAnswerIneligibleValue = (isIneligible: boolean) => {
    setIsAnswerIneligible(isIneligible);
  };

  const resetState = () => {
    setModalOpen(DEFAULT_STATE.isModalOpen);
    setPrePurchaseQuizAvailable(DEFAULT_STATE.isPrePurchaseQuizAvailable);
    setCurrentStep(DEFAULT_STATE.currentStep);
    setNumberOfSteps(DEFAULT_STATE.numberOfSteps);
    setIsQuizStarted(DEFAULT_STATE.isQuizStarted);
    setIsQuizEnded(DEFAULT_STATE.isQuizEnded);
    setPersonalData(DEFAULT_STATE.personalData);
    setEmailData(DEFAULT_STATE.emailData);
    setAreControlsVisible(DEFAULT_STATE.areControlsVisible);
    setIsAnswerIneligible(DEFAULT_STATE.isAnswerIneligible);
  };

  useEffect(() => {
    if (isAnswerIneligible) {
      tracking.eligibilityEvent(!isAnswerIneligible);
    }
  }, [isAnswerIneligible]);

  return (
    <AsyncProgramPPQContext.Provider
      value={{
        // State values
        isModalOpen,
        isPrePurchaseQuizAvailable,
        numberOfSteps,
        currentStep,
        isQuizStarted,
        isQuizEnded,
        personalData,
        emailData,
        areControlsVisible,
        isAnswerIneligible,
        questionsAndAnswers,
        // Functions
        toggleControlsVisibility,
        toggleModal,
        setIsPrePurchaseQuizAvailable,
        nextStep,
        prevStep,
        setNumberOfSteps: setNumberOfStepsValue,
        startQuiz,
        endQuiz,
        setPersonalData: setPersonalDataValue,
        setEmailData: setEmailDataValue,
        setIsAnswerIneligible: setIsAnswerIneligibleValue,
        setQuestionsAndAnswers,
        resetState,
      }}
    >
      {children}
    </AsyncProgramPPQContext.Provider>
  );
};
